import React, {ReactElement, useEffect, useState} from "react";

/** Context that provides updating information about the current screen category and width.
 * The whole App is wrapped in the ScreenSizeProvider, allowing access to the context from anywhere.
 * Usage example:
 *
 * import {ScreenSize} from "./ScreenSize";
 * function SizeComponent() {
 *   const screenSize = useContext(ScreenSize);
 *   return (<div>{screenSize.category}</div>)
 * }
 */
export const ScreenSize = React.createContext<ScreenSizeInfo>({
  category: "Mobile",
  width: 400,
});

type ScreenSizeInfo = {
  /** Screen type based solely on its width */
  category: "Mobile" | "Tablet" | "Desktop" | "Big Desktop";
  /** Screen width in px */
  width: number;
};

function ScreenSizeProvider({children}: {children: ReactElement}) {
  const getSize: () => ScreenSizeInfo = () => {
    const {innerWidth: width} = window;
    return {
      category:
        width >= 1536
          ? "Big Desktop"
          : width >= 1304
            ? "Desktop"
            : width >= 600
              ? "Tablet"
              : "Mobile",
      width,
    };
  };
  const [size, setSize] = useState<ScreenSizeInfo>(getSize());

  useEffect(() => {
    const handleResize = () => {
      setSize(getSize());
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return <ScreenSize.Provider value={size}>{children}</ScreenSize.Provider>;
}

export default ScreenSizeProvider;
